// frontend/src/context/ResultsContext.js
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CACHE_DURATIONS } from '../config/cacheConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const RESULTS_PER_PAGE = 10;
const CACHE_DURATION = CACHE_DURATIONS.RESULTS;

const ResultsContext = createContext(null);

/**
 * ResultsProvider - Caches results across page navigation
 * Prevents re-fetching when user navigates away and comes back
 */
export function ResultsProvider({ children }) {
  // Results state
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters state
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');

  // Votes state
  const [votes, setVotes] = useState({});
  const [userVoted, setUserVoted] = useState(new Set());

  // Cache metadata
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [lastFilterKey, setLastFilterKey] = useState('');

  // ✅ Ref to prevent duplicate calls
  const isLoadingRef = useRef(false);

  // Generate filter key for cache validation
  const getFilterKey = useCallback(() => {
    return JSON.stringify({ filter, categoryFilter, modelFilter, sortFilter });
  }, [filter, categoryFilter, modelFilter, sortFilter]);

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    const filterKey = getFilterKey();
    const cacheAge = Date.now() - lastFetchTime;
    return (
      results.length > 0 &&
      filterKey === lastFilterKey &&
      cacheAge < CACHE_DURATION
    );
  }, [results.length, lastFetchTime, lastFilterKey, getFilterKey]);

  // Load results with caching
  const loadResults = useCallback(async (reset = false) => {
    const filterKey = getFilterKey();

    // ✅ FIX 1: Cache check only for initial load (reset=true), not for "load more"
    if (reset && isCacheValid()) {
      console.log('📦 Using cached results');
      return;
    }

    // ✅ FIX 2: Prevent duplicate "load more" calls using ref
    if (!reset && isLoadingRef.current) {
      console.log('⏳ Already loading, skipping...');
      return;
    }

    // Set loading ref immediately (before async)
    isLoadingRef.current = true;

    if (reset) {
      setLoading(true);
      setResults([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : results.length;

      // STATIC MODE: load from local JSON file and filter/sort locally
      const response = await fetch('/data/results.json');
      const allData = await response.json();
      let allResults = Array.isArray(allData) ? allData : (allData.results || []);

      // Apply filters locally
      if (categoryFilter !== 'all') {
        allResults = allResults.filter(run =>
          (run.results || []).some(r => r.category === categoryFilter)
        );
      }
      if (modelFilter !== 'all') {
        allResults = allResults.filter(run =>
          (run.results || []).some(r => r.model_id === modelFilter || r.model_name === modelFilter)
        );
      }

      // Apply sort locally
      if (sortFilter === 'newest' || !sortFilter) {
        allResults.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      } else if (sortFilter === 'oldest') {
        allResults.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      }

      // Paginate locally
      const pageResults = allResults.slice(offset, offset + RESULTS_PER_PAGE);
      const validResults = pageResults.filter(run => run && run.run_id);

      setResults(prev => reset ? validResults : [...prev, ...validResults]);
      setHasMore(validResults.length >= RESULTS_PER_PAGE);

      if (validResults.length > 0) {
        const newVotes = {};
        const newUserVoted = reset ? new Set() : new Set(userVoted);

        validResults.forEach(run => {
          if (!run || !run.run_id) return;
          newVotes[run.run_id] = {};

          (run.results || []).forEach(result => {
            if (result) {
              const voteCount = typeof result.vote_count === 'number'
                ? result.vote_count
                : parseInt(result.vote_count, 10) || 0;
              newVotes[run.run_id][result.id] = { votes: voteCount };
            }
          });

          if (run.user_vote) {
            newUserVoted.add(run.user_vote);
          }
        });

        setVotes(prev => reset ? newVotes : { ...prev, ...newVotes });
        setUserVoted(newUserVoted);
      }

      // Update cache metadata
      if (reset) {
        setLastFetchTime(Date.now());
        setLastFilterKey(filterKey);
      }

    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      // ✅ Reset loading ref
      isLoadingRef.current = false;
    }
  }, [filter, categoryFilter, modelFilter, sortFilter, results.length, userVoted, isCacheValid, getFilterKey]);

  // Clear cache (for manual refresh)
  const clearCache = useCallback(() => {
    setLastFetchTime(0);
    setLastFilterKey('');
  }, []);

  // Force refresh
  const refresh = useCallback(() => {
    clearCache();
    loadResults(true);
  }, [clearCache, loadResults]);

  const value = {
    // Results
    results,
    setResults,
    hasMore,
    loading,
    loadingMore,

    // Filters
    filter,
    setFilter,
    categoryFilter,
    setCategoryFilter,
    modelFilter,
    setModelFilter,
    sortFilter,
    setSortFilter,

    // Votes
    votes,
    setVotes,
    userVoted,
    setUserVoted,

    // Actions
    loadResults,
    loadMore: () => loadResults(false),
    refresh,
    clearCache,
    isCacheValid
  };

  return (
    <ResultsContext.Provider value={value}>
      {children}
    </ResultsContext.Provider>
  );
}

// Hook to use results context
export function useResults() {
  const context = useContext(ResultsContext);
  if (!context) {
    throw new Error('useResults must be used within a ResultsProvider');
  }
  return context;
}

export default ResultsContext;